// The third gate.
//
// Sits inside RequireVault, so onboarding runs against an open vault and can write immediately.
// The check is server-side (does a user_cohort row exist) rather than a localStorage flag, because
// a flag would re-run the flow on every new device and, worse, would let a cleared browser
// overwrite answers already given.
//
// Failing open is deliberate. If the cohort endpoint is unreachable, the person gets the app
// rather than a spinner in front of their journal: a demographic question is never worth blocking
// someone's writing over.

import { useEffect, useState, type ReactNode } from 'react';
import { getCohort } from '../api/cohort';
import { FullPageSpinner } from '../components/ui';
import { OnboardingFlow } from './OnboardingFlow';

type Status = 'checking' | 'needed' | 'done';

export function RequireOnboarding({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('checking');

  useEffect(() => {
    const controller = new AbortController();
    getCohort(controller.signal)
      .then((cohort) => setStatus(cohort.completed ? 'done' : 'needed'))
      .catch(() => setStatus('done'));
    return () => controller.abort();
  }, []);

  if (status === 'checking') return <FullPageSpinner />;
  if (status === 'needed') return <OnboardingFlow onDone={() => setStatus('done')} />;
  return <>{children}</>;
}
